/**
 * DeleteAccount — public landing page for requesting account deletion.
 *
 * Reachable at `https://correlate.online/delete-account` without sign-in, per
 * Google Play's data-safety requirement (Play needs a public URL any user —
 * even a former one who deleted the app — can visit to learn how to delete
 * their data). Signed-in users get a one-tap delete button; signed-out users
 * get instructions to sign in first (or contact us if they no longer have
 * access to their account).
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Trash2 } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";

export default function DeleteAccount() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  const canDelete = user && confirmText.trim().toUpperCase() === "DELETE MY ACCOUNT";

  const onDelete = async () => {
    if (!canDelete || busy) return;
    if (!window.confirm(
      "This will permanently delete your Correlate account, posts, comments, saves, messages, and all associated data. This cannot be undone. Are you sure?"
    )) return;
    setBusy(true);
    try {
      await api.delete("/users/me");
      try { await signOut?.(); } catch (e) { if (process.env.NODE_ENV === "development") console.debug("[deleteaccount] soft-fail:", e?.message); }
      toast.success("Your account has been deleted.");
      // Small delay so the toast is visible before navigate.
      setTimeout(() => { navigate("/"); }, 800);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete. Try again in a moment.");
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="delete-account-page"
      className="max-w-2xl mx-auto px-6 py-16 sm:py-24"
    >
      <div className="label mb-4 text-muted-foreground">Account</div>
      <h1 className="serif text-4xl sm:text-5xl text-foreground mb-6 leading-tight">
        Delete your Correlate account
      </h1>
      <p className="serif italic text-lg text-muted-foreground mb-10 leading-relaxed">
        This page permanently removes your account and every trace of your presence in the archive.
      </p>

      <div className="hairline p-6 sm:p-8 space-y-6">
        <div>
          <div className="label mb-3 text-[hsl(var(--accent))]">What gets deleted</div>
          <ul className="space-y-2 text-[15px] text-foreground/90 leading-relaxed">
            <li>&#8226; Your account (email, name, profile photo, handle, bio)</li>
            <li>&#8226; Every post you&apos;ve published to Weekly</li>
            <li>&#8226; Every private save and shelf</li>
            <li>&#8226; Every comment and reaction</li>
            <li>&#8226; Every direct message you&apos;ve sent</li>
            <li>&#8226; Your follows, followers, notifications, and pinned traditions</li>
            <li>&#8226; Your Oracle session history</li>
          </ul>
        </div>

        <div>
          <div className="label mb-3 text-muted-foreground">Retention</div>
          <p className="text-[14px] text-muted-foreground leading-relaxed">
            Deletion is <b>immediate and permanent</b> — nothing is soft-deleted, nothing is kept in a
            recovery bin. Once the request completes, we cannot restore your data. Server-side backups
            are rotated out within 30 days, after which even our infrastructure cannot recover it.
          </p>
        </div>

        <div>
          <div className="label mb-3 text-muted-foreground">If you only want to delete some data</div>
          <p className="text-[14px] text-muted-foreground leading-relaxed">
            You can remove individual posts, comments, saves, and messages from within the app without
            deleting your whole account. Sign in, navigate to the item, and tap the trash icon.
          </p>
        </div>

        <hr className="border-[hsl(var(--border))]" />

        {user ? (
          <div>
            <div className="flex items-start gap-3 mb-4 p-4 border border-[hsl(var(--destructive)/0.4)] bg-[hsl(var(--destructive)/0.05)]">
              <AlertTriangle size={20} className="text-[hsl(var(--destructive))] flex-shrink-0 mt-0.5" />
              <p className="text-[14px] text-foreground/90 leading-relaxed">
                You&apos;re signed in as <b>{user.email || user.name}</b>. Deleting will end your session and
                remove everything above. To confirm, type <b>DELETE MY ACCOUNT</b> below.
              </p>
            </div>
            <input
              data-testid="delete-account-confirm"
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Type DELETE MY ACCOUNT to enable"
              className="input-line w-full text-[15px] mb-4"
              autoComplete="off"
            />
            <button
              type="button"
              data-testid="delete-account-submit"
              onClick={onDelete}
              disabled={!canDelete || busy}
              className="mono text-[10px] uppercase tracking-[0.22em] px-5 py-3 border-2 border-[hsl(var(--destructive))] text-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive)/0.08)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-2"
            >
              <Trash2 size={13} />
              {busy ? "Deleting…" : "Delete my account permanently"}
            </button>
          </div>
        ) : (
          <div>
            <p className="text-[15px] text-foreground/90 leading-relaxed mb-4">
              To delete your account, please sign in first.
            </p>
            <a
              href="/login?next=/delete-account"
              data-testid="delete-account-signin"
              className="mono text-[10px] uppercase tracking-[0.22em] px-5 py-3 border-2 border-[hsl(var(--primary))] text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary)/0.08)] transition-colors inline-flex items-center gap-2"
            >
              Sign in to continue
            </a>
            <p className="mt-6 text-[13px] text-muted-foreground leading-relaxed">
              No longer have access to your account? Email us at{" "}
              <a href="mailto:privacy@correlate.online" className="text-[hsl(var(--primary))] underline">
                privacy@correlate.online
              </a>{" "}
              from the email address you signed up with, and we&apos;ll process the deletion manually within
              7 days.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
